/** @jsxImportSource react */
import { useState } from 'react'
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, MenuItem, Paper, Select,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip,
} from '@mui/material'
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'
import AddIcon from '@mui/icons-material/Add'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { platformAPI } from '../../api/crm'
import { getErrorMessage } from '../../utils/apiErrors'
import { formatDate } from '../../utils/helpers'
import { useNotify } from '../../components/common/NotificationProvider'
import PageHeader from '../../components/common/PageHeader'
import Loading from '../../components/common/Loading'
import EmptyState from '../../components/common/EmptyState'
import InviteLinkDialog from '../../components/common/InviteLinkDialog'

const STATUSES = ['ACTIVE', 'INACTIVE', 'LOCKED']

const TenantsPage = () => {
  const queryClient = useQueryClient()
  const notify = useNotify()
  const navigate = useNavigate()
  const [createOpen, setCreateOpen] = useState(false)
  const [link, setLink] = useState(null)
  const [deleting, setDeleting] = useState(null)
  const { data: tenants, isLoading, error } = useQuery({ queryKey: ['platform', 'tenants'], queryFn: platformAPI.listTenants, retry: false })

  const statusMutation = useMutation({
    mutationFn: ({ tenantId, status }) => platformAPI.updateStatus(tenantId, status),
    onSuccess: (tenant) => {
      notify(`${tenant.name} is now ${tenant.status.toLowerCase()}`)
      queryClient.invalidateQueries({ queryKey: ['platform'] })
      queryClient.invalidateQueries({ queryKey: ['me'] })
    },
    onError: (e) => notify(getErrorMessage(e), 'error'),
  })

  if (isLoading) return <Loading />
  if (error) return <Alert severity="error">{getErrorMessage(error)}</Alert>

  return (
    <>
      <PageHeader
        title="Tenants"
        subtitle="Every workspace on the platform. Locking a tenant blocks all of its users. New workspaces start a free trial."
        actions={(
          <>
            <Button onClick={() => navigate('/platform/billing')}>Plans & billing</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>New tenant</Button>
          </>
        )}
      />
      {tenants.length === 0 ? (
        <Paper variant="outlined">
          <EmptyState title="No tenants yet" description="Create the first workspace and assign its admin." />
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>ID</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((t) => (
                <TableRow key={t.id} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{t.name}</TableCell>
                  <TableCell>
                    <Select
                      value={t.status}
                      variant="standard"
                      disableUnderline
                      onChange={(e) => statusMutation.mutate({ tenantId: t.id, status: e.target.value })}
                      sx={{ fontSize: 'inherit' }}
                    >
                      {STATUSES.map((s) => <MenuItem key={s} value={s}>{s.toLowerCase()}</MenuItem>)}
                    </Select>
                  </TableCell>
                  <TableCell>{formatDate(t.createdOn)}</TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', color: 'text.secondary', fontSize: 12 }}>{t.id}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={t.status === 'ACTIVE' ? 'Switch the workspace off (inactive or locked) before deleting it' : 'Delete permanently'}>
                      <span>
                        <IconButton disabled={t.status === 'ACTIVE'} onClick={() => setDeleting(t)} aria-label={`Delete ${t.name}`}>
                          <DeleteForeverOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <CreateTenantDialog open={createOpen} onClose={() => setCreateOpen(false)} onInvite={setLink} />
      <InviteLinkDialog link={link} onClose={() => setLink(null)} />
      {deleting && <DeleteTenantDialog tenant={deleting} onClose={() => setDeleting(null)} />}
    </>
  )
}

/** Permanent: every record, file and member link of the workspace. The exact name confirms. */
const DeleteTenantDialog = ({ tenant, onClose }) => {
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [typed, setTyped] = useState('')
  const mutation = useMutation({
    mutationFn: () => platformAPI.deleteTenant(tenant.id, typed),
    onSuccess: () => {
      notify(`${tenant.name} deleted`)
      queryClient.invalidateQueries({ queryKey: ['platform'] })
      queryClient.invalidateQueries({ queryKey: ['me'] })
      onClose()
    },
  })
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete {tenant.name} permanently?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          All of its records, files, tasks, emails, forms and member access are deleted for good. People’s accounts stay
          (they may belong to other workspaces). Export the workspace first if the customer wants their data.
        </DialogContentText>
        {mutation.error && <Alert severity="error" sx={{ mb: 2 }}>{getErrorMessage(mutation.error)}</Alert>}
        <TextField fullWidth autoFocus label={`Type “${tenant.name}” to confirm`} value={typed} onChange={(e) => setTyped(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
        <Button color="error" variant="contained" disabled={typed !== tenant.name || mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const CreateTenantDialog = ({ open, onClose, onInvite }) => {
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [name, setName] = useState('')
  const [adminEmail, setAdminEmail] = useState('')

  const mutation = useMutation({
    mutationFn: platformAPI.createTenant,
    onSuccess: ({ tenant, adminInvite }) => {
      notify(`${tenant.name} created`)
      // someone else is the admin: they join through the invite link
      if (adminInvite) onInvite(adminInvite)
      queryClient.invalidateQueries({ queryKey: ['platform'] })
      queryClient.invalidateQueries({ queryKey: ['me'] })
      setName('')
      setAdminEmail('')
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ component: 'form', onSubmit: (e) => { e.preventDefault(); mutation.mutate({ name: name.trim(), adminEmail: adminEmail.trim() }) } }}>
      <DialogTitle>New tenant</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {mutation.error && <Alert severity="error">{getErrorMessage(mutation.error)}</Alert>}
          <TextField label="Workspace name" required autoFocus value={name} onChange={(e) => setName(e.target.value)} />
          <TextField label="Admin email" type="email" required value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} helperText="Your own email makes you admin right away; anyone else gets an invite link." />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={mutation.isPending}>Create</Button>
      </DialogActions>
    </Dialog>
  )
}

export default TenantsPage
